import { defineStore } from 'pinia'

import {
    exportAggregatedData,
    generateLogXLSXData,
    getLogExportStatuses,
    generateLogCSVData,
    downloadLogData,
} from '@/api/call-tracking/reporting/reporting-routes'

import { downloadFile, downloadFileFromBlob } from '@/helpers/app-helper'

import { useDefaultStore } from '@/store/defaultStore'
import { usePermissionsStore } from '@/store/permissionsStore'

import { exportType } from '@/constants/exportType'
import { useReportingStore } from '@/store/call-tracking/reporting/reportingStore'

export const useDownloadReportingStore = defineStore('downloadReportingStore', {
    state: () => ({
        downloadingAggregate: false,
        downloadingAggregateStatus: null,
        downloadingLog: false,
        downloadingLogStatus: null,
        aggregateFilename: '',
        aggregateSize: 0,
        logSize: 0,
        logFilename: '',
        logFileSlug: null,
        logProgress: 0,
        aggregateProgress: 0,
        statuses: {
            inProgress: 'in_process',
            success: 'success',
            failed: 'failed',
        },
        logTimer: null,
        logAttempt: 0,
    }),

    actions: {
        buildSendParams(paramsForSend) {
            if (paramsForSend.parameters) {
                paramsForSend.parameters = Object.entries(paramsForSend.parameters).reduce((acc, [key, value]) => {
                    acc[`[${key}]`] = value
                    return acc
                }, {})
            }

            const reportingStore = useReportingStore()

            return {
                ...paramsForSend,
                ...reportingStore.userSelected,
            }
        },

        async goToExportAggregatedData(params) {
            const defaultStore = useDefaultStore()

            this.downloadingAggregate = true
            this.downloadingAggregateStatus = this.statuses.inProgress
            this.aggregateFilename = params.type === exportType.CSV
                ? 'calls-aggregated-export.csv'
                : 'calls-aggregated-export.xlsx'

            const res = await exportAggregatedData({
                params: this.buildSendParams(params),
                onDownloadProgress: this.onDownloadAggregatedProgress,
            })

            if (res?.data && res.data.success === false) {
                !!res.data.message && defaultStore.setErrorMessage({ message: res.data.message })
                this.downloadingAggregateStatus = this.statuses.failed
            } else if (res?.success === false) {
                !!res.message && defaultStore.setErrorMessage({ message: res.message })
                this.downloadingAggregateStatus = this.statuses.failed
            } else {
                this.downloadingAggregateStatus = this.statuses.success
                downloadFile(res, params.type)
            }

            this.downloadingAggregate = false
            this.aggregateSize = 0
        },

        removeAggregateStatus() {
            this.downloadingAggregateStatus = null
        },

        removeLogStatus() {
            this.downloadingLogStatus = null
        },

        onDownloadAggregatedProgress(progressEvent) {
            if (progressEvent.loaded) {
                this.aggregateSize = Math.round(progressEvent.loaded * 0.000001)
            }
        },

        async goToGenerateLogXLSXData(params) {
            this.downloadingLog = true
            this.downloadingLogStatus = this.statuses.inProgress
            this.logSize = 0

            const { success, payload, message } = await generateLogXLSXData(this.buildSendParams(params))

            if (success) {
                this.logFilename = payload.name
                this.logFileSlug = payload.slug
                this.checkLogStatus(payload.status)
            } else if (message === 'Report generation is in progress') {
                this.goToCheckLogStatuses()
            } else {
                const defaultStore = useDefaultStore()
                defaultStore.setErrorMessage({ message })
                this.downloadingLog = false
                this.downloadingLogStatus = this.statuses.failed
                this.logFilename = 'call-log-export.xlsx'
            }
        },

        async goToGenerateLogCSVData(params) {
            this.downloadingLog = true
            this.downloadingLogStatus = this.statuses.inProgress
            this.logSize = 0

            const { success, payload, message } = await generateLogCSVData(this.buildSendParams(params))

            if (success) {
                this.logFilename = payload.name
                this.logFileSlug = payload.slug
                this.checkLogStatus(payload.status)
            } else if (message === 'Report generation is in progress') {
                this.goToCheckLogStatuses()
            } else {
                const defaultStore = useDefaultStore()
                defaultStore.setErrorMessage({ message })
                this.downloadingLog = false
                this.downloadingLogStatus = this.statuses.failed
                this.logFilename = 'call-log-export.csv'
            }
        },

        checkLogStatus(status) {
            if (!status) {
                this.downloadingLog = false
                this.downloadingLogStatus = null
                this.resetLogTimer()
                return
            }

            switch (status) {
            case this.statuses.inProgress:
                this.downloadingLog = true
                this.downloadingLogStatus = this.statuses.inProgress
                this.setTimerForCheckLogStatus()
                break
            case this.statuses.success:
                this.downloadingLogStatus = this.statuses.success
                this.resetLogTimer()
                this.goToDownloadLog()
                break
            case this.statuses.failed:
                this.downloadingLog = false
                this.downloadingLogStatus = this.statuses.failed
                this.resetLogTimer()
                break
            }
        },

        setTimerForCheckLogStatus() {
            clearTimeout(this.logTimer)

            const delays = [2000, 5000, 10000]
            const delay = delays[Math.min(this.logAttempt, delays.length - 1)]

            this.logAttempt++

            this.logTimer = setTimeout(() => {
                this.downloadingLog = true
                this.goToCheckLogStatuses()
            }, delay)
        },

        resetLogTimer() {
            clearTimeout(this.logTimer)
            this.logAttempt = 0
        },

        async goToCheckLogStatuses() {
            const permissionsStore = usePermissionsStore()

            if (!permissionsStore.permissions.reporting.download) {
                return
            }

            const { success, payload, message } = await getLogExportStatuses()

            if (success) {
                this.logFilename = payload?.name
                this.logFileSlug = payload?.slug
                this.checkLogStatus(payload?.status)
            } else {
                const defaultStore = useDefaultStore()
                defaultStore.setErrorMessage({ message })
                this.resetLogTimer()
                this.downloadingLog = false
                this.downloadingLogStatus = this.statuses.failed
            }
        },

        async goToDownloadLog() {
            const defaultStore = useDefaultStore()
            this.downloadingLog = true

            const res = await downloadLogData(this.logFileSlug)

            if (res?.success === false) {
                !!res.message && defaultStore.setErrorMessage({ message: res.message })
                this.downloadingLogStatus = this.statuses.failed
            } else {
                this.downloadingLogStatus = this.statuses.success
                downloadFileFromBlob(res, this.logFilename)
            }

            this.downloadingLog = false
        },
    },
})
